import { forwardRef, useState, useEffect } from 'react';
import { cn } from '../../lib/utils';

interface FormCpfCnpjProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type: 'cpf' | 'cnpj';
  error?: boolean;
  validationError?: string;
  required?: boolean;
}

const formatCpfCnpj = (value: string, type: 'cpf' | 'cnpj'): string => {
  const numbers = value.replace(/\D/g, '');

  if (type === 'cpf') {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }

  return numbers
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
};

const FormCpfCnpj = forwardRef<HTMLInputElement, FormCpfCnpjProps>(
  (
    { className, type, error, validationError, defaultValue, required, onChange, ...props },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(defaultValue?.toString() || '');
    const [inputError, setInputError] = useState<string | null>(null);

    useEffect(() => {
      setInputError(validationError || null);
    }, [validationError]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      onChange?.(e);
    };

    const handleBlur = () => {
      const formattedValue = formatCpfCnpj(inputValue, type);
      setInputValue(formattedValue);
      if (onChange) {
        const event = {
          target: { value: formattedValue, name: props.name || '' },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
    };

    const pattern =
      type === 'cpf'
        ? '^\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2}$'
        : '^\\d{2}\\.\\d{3}\\.\\d{3}/\\d{4}-\\d{2}$';

    const inputClassName = cn(
      'mt-1 block w-full rounded-md shadow-sm sm:text-sm transition-colors duration-200',
      inputError || error
        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
        : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500',
      className
    );

    return (
      <div>
        <input
          ref={ref}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          pattern={pattern}
          placeholder={type === 'cpf' ? '000.000.000-00' : '00.000.000/0000-00'}
          title={
            type === 'cpf'
              ? 'Digite um CPF válido (000.000.000-00)'
              : 'Digite um CNPJ válido (00.000.000/0000-00)'
          }
          className={inputClassName}
          required={required}
          {...props}
        />
        {inputError && <p className="mt-1 text-sm text-red-600">{inputError}</p>}
      </div>
    );
  }
);

FormCpfCnpj.displayName = 'FormCpfCnpj';

export { FormCpfCnpj };
